import { useCallback, useMemo, useState } from 'react'
import { TableReportCouponsColumnsDefinition } from './components/TableReportCouponsColumnsDefinition'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { useReportCoupons } from '@/app/hooks/useReportCoupons'
import * as XLSX from 'xlsx'
import {
  ReportCoupons,
  ReportCouponsParams,
} from '@/app/services/reportService/reportCoupons'
import { reportService } from '@/app/services/reportService'
import { FiltersType } from '../components/FilterReports'
import { useAuth } from '@/app/hooks/useAuth'

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportWithdrawsController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } = useReportCoupons()
  const { isLeader, isAdmin, isManager, isSupervisor } = useAuth()

  const { columns } = TableReportCouponsColumnsDefinition()

  const table = useReactTable({
    data: data?.coupons || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  const fetchAllData = useCallback(async (): Promise<ReportCoupons[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    const batchSize = AMOUNT_REPORTS_FETCH
    const total = data.pagination.total
    let currentSkip = 0
    let allData: ReportCoupons[] = []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const batches: Promise<any>[] = []

    let completedBatches = 0

    while (total > currentSkip) {
      const exportParams: ReportCouponsParams = {
        ...params,
        limit: batchSize,
        skip: currentSkip,
      }

      const batchPromise = reportService
        .reportCoupons(exportParams)
        .then((batchData) => {
          allData = [...allData, ...batchData.coupons]

          completedBatches += 1

          const progress = Number(
            Math.min(
              ((completedBatches * batchSize) / total) * 100,
              100,
            ).toFixed(0),
          )
          setExportProgress(progress)
        })
        .catch((error) => {
          console.error('Error fetching batch data:', error)
        })

      batches.push(batchPromise)

      currentSkip += batchSize
    }

    try {
      await Promise.all(batches)
    } catch (error) {
      console.error('Error awaiting batch resolutions:', error)
    }

    setIsExporting(false)
    return allData
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    setIsExporting(true)
    const fetchedData = await fetchAllData()

    const formattedData = fetchedData.map((coupon) => ({
      seller: coupon.user || '',
      code: coupon.code || '',
      prizedraw: coupon.prizedraw || '',
      date: format(coupon.createdAt, 'dd/MM/yyyy, HH:mm') || '',
    }))

    setIsExporting(false)

    if (formattedData.length > 0) {
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(formattedData)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
      XLSX.utils.sheet_add_aoa(worksheet, [
        ['Vendedor', 'Número', 'Sorteio', 'Data'],
      ])
      XLSX.writeFile(workbook, 'coupons-reports.xlsx')
    }
  }, [fetchAllData])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ['Date']

    if (isAdmin)
      result.push('Managers', 'Supervisors', 'Leaders', 'Teams', 'Sellers')
    if (isManager) result.push('Supervisors', 'Leaders', 'Teams', 'Sellers')
    if (isSupervisor) result.push('Leaders', 'Teams', 'Sellers')
    if (isLeader) result.push('Sellers')

    result.push('SortByUser')

    return result
  }, [isAdmin, isManager, isSupervisor, isLeader])

  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
